import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { calculateMinutesElapsed } from "@src/appV2/redesign/ArrivalTime/calculateMinutesElapsed";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { TotalPayLabel } from "@src/appV2/redesign/Pay/TotalPayLabel";
import { getFormattedShiftPayBreakdown } from "@src/appV2/redesign/Shift/getFormattedShiftPayBreakdown";
import { InstantPayIndicator } from "@src/appV2/redesign/WorkerShifts/components/InstantPayIndicator";
import { useWorkplaceGuide } from "@src/appV2/redesign/Workplace/useWorkplaceGuide";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { parseISO } from "date-fns";

import { Button } from "../../../components/Button";
import { StickyFooter } from "../../../components/StickyFooter";
import { SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI } from "../constants";
import { useFooterPrimaryAction } from "../hooks/useShiftActionVisibility";

export interface InstantPayFooterProps {
  shift: WorkerShift;
  currentDate: Date;
  onClockIn: () => void;
  onUpdateETA: () => void;
  onCancelShift: () => void;
  onTransferShift: () => void;
  onSentHome: () => void;
}

function InstantPayFooter(props: InstantPayFooterProps) {
  const { shift, currentDate, onClockIn, onUpdateETA, onCancelShift, onTransferShift, onSentHome } =
    props;

  const primaryAction = useFooterPrimaryAction({ shift, currentDate });

  const shiftStartDate = isDefined(shift.start) ? parseISO(shift.start) : undefined;

  const hourlyPay = shift.offer?.pay.value ?? shift.finalPay ?? 0;
  const minutesElapsed = isDefined(shiftStartDate)
    ? calculateMinutesElapsed(shiftStartDate, currentDate)
    : 0;

  const payBreakdown = getFormattedShiftPayBreakdown(
    shift.offer?.pay.value,
    shift.finalPay,
    (shift.originalAmount ?? 0) - (minutesElapsed / 60) * hourlyPay
  );

  // Prefetch clock in guide so that the button state is ready when someone opens the bottom sheet
  useWorkplaceGuide({ workplaceId: shift.facilityId, category: "clock_in" });

  const renderPrimaryButton = () => {
    if (!primaryAction) {
      return null;
    }

    const { action, enabled } = primaryAction;

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (action) {
      case "transferShift": {
        return (
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={!enabled}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_TRANSFER_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onTransferShift();
            }}
          >
            Transfer shift
          </Button>
        );
      }

      case "cancelShift": {
        return (
          <Button
            fullWidth
            variant="outlined"
            size="large"
            sx={{
              color: (theme) => theme.palette.error.light,
              borderColor: (theme) => theme.palette.border?.primary,
            }}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CANCEL_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onCancelShift();
            }}
          >
            Cancel shift
          </Button>
        );
      }

      case "clockIn": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CLOCK_IN_NOW_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onClockIn();
            }}
          >
            Clock in now
          </Button>
        );
      }

      case "updateEta": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_LATE_FOR_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onUpdateETA();
            }}
          >
            Update ETA
          </Button>
        );
      }

      case "sentHome": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_SENT_HOME_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onSentHome();
            }}
          >
            I was sent home
          </Button>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <StickyFooter>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ paddingX: 2, marginBottom: 5 }}
      >
        <HourlyPayLabel size="large" hourlyPay={payBreakdown?.hourlyPay} />
        <Stack direction="row" spacing={2} alignItems="center">
          <TotalPayLabel totalPay={payBreakdown?.totalPay} />
          {isDefined(shift) && shift.isInstantPay && <InstantPayIndicator />}
        </Stack>
      </Stack>

      {renderPrimaryButton()}
    </StickyFooter>
  );
}

export { InstantPayFooter };
